import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import Input from "./Input";
import Dialog from "../dialogs/Dialog";
import Button from "../buttons/Button";
import { useValidation } from "../utils/ValidationProvider";
import { Editor, EditorTools, EditorToolsSettings } from '@progress/kendo-react-editor';
import { defaultMarkdownParser, defaultMarkdownSerializer } from "prosemirror-markdown";

import { buildUrl } from "../utils/helper";
import "./Textarea.scss";

const { Bold, Italic, Link, Unlink, InsertImage, Undo, Redo, OrderedList, UnorderedList, createFormatBlockDropDownList } = EditorTools;

// creating custom FormatBlock with slovak localization
const sk = {
  "p": "Paragraf",
  "h1": "Nadpis 1",
  "h2": "Nadpis 2",
  "h3": "Nadpis 3",
  "h4": "Nadpis 4",
  "h5": "Nadpis 5",
  "h6": "Nadpis 6"
};
let customFormatBlockProps = EditorToolsSettings.formatBlock;
customFormatBlockProps.items.forEach(x => x.text = sk[x.value]);
const CustomFormatBlock = createFormatBlockDropDownList(customFormatBlockProps);
//

/**
 * Text area component
 * @module
 * @param {string} name - Name
 * @param {func} onChange - Function invoked on value change
 * @param {bool} [disabled] - Is textarea readonly?
 * @param {string} [value] - Current value
 * @param {bool} [required] - Required
 * @param {string} title - Textarea title
 * @param {number} [tabIndex] - Textarea tab index
 * @param {bool} [readOnly] - Read only
 * @param {number} [minLength] - Minimum length
 * @param {number} [maxLength] - Maximum length
 * @param {bool} [defaultExpanded] - Expands the textarea
 */
const TextareaComponent = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState("");
  const [editorValue, setEditorValue] = useState(null);
  const [dialogValue, setDialogValue] = useState("");
  const [preview, setPreview] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [toolbarHover, setToolbarHover] = useState(false);
  const [toolbar, setToolbar] = useState(false);

  const editorRef = useRef(null);
  const toolbarTimeout = useRef(null);

  const field = useMemo(() => ({
    field: props.name,
    title: props.title,
    type: props.type,
    min: props.min,
    max: props.max,
    minLength: props.minLength,
    maxLength: props.maxLength,
    validationMessage: props.validationMessage,
    pattern: props.pattern,
    required: props.required,
    disabled: props.disabled,
    recommended: props.recommended,
    validationAllowed: props.validationAllowed,
    isInvalid: props.isInvalid
  }), [props.disabled, props.max, props.maxLength, props.min, props.minLength, props.name, props.pattern, props.required, props.title, props.type, props.validationMessage, props.recommended, props.validationAllowed, props.isInvalid]);

  const { validate, setValidationValue } = useValidation(field, props.value);

  useEffect(() => {
    setValue(props.value || "");
    if (props.richText && !preview) {
      setEditorValue(defaultMarkdownParser.parse(props.value || ""));
    }
  }, [props.value, preview, props.richText]);
  
  useEffect(() => {
    setTimeout(() => setRefresh(prev => !prev));
  }, [preview]);

  const handleOpen = () => {
    setIsOpen(true);
    setDialogValue(props.value || "");
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleConfirm = () => {
    validate();
    handleClose();
    props.onChange(props.name, dialogValue);
  };

  const handleClick = (e) => {
    if (e.detail === 2) {
      handleOpen();
    }
  };

  const handleInsertClick = () => {
    let _value = "";
    if (props.value) {
      if (props.value.indexOf(props.insertValue) !== -1) {
        _value = props.value;
      } else {
        _value = `${props.insertValue} - ${props.value}`;
      }
    } else {
      _value = props.insertValue;
    }
    props.onChange(props.name, _value);
  };

  const onBlur = () => {
    validate();
    if (props.onBlur) {
      props.onBlur();
    }
  };

  const handleChange = (name, value) => {
    setValue(value);
    setValidationValue(value)
    props.onChange(name, value);
  };

  const buildSwitch = () => {
  return (
    <>
      {props.richText && (value || preview) && 
        <Button
          buttonWrapperClassName="preview-switch"
          className="text-button"
          title={!preview ? "Text s formátovaním" : "Text bez formátovania"}
          onClick={() => setPreview(!preview)}
          imageUrl={buildUrl(preview ? 'assets/icons/toolbar/visual-rte.svg' : 'assets/icons/toolbar/text-rte.svg')}
          // HACK: mizol tooltip pri kliknuti, target element bol null, refresh pocka jeden render call aby ref pre div nebolo null
          _prop_to_refresh={refresh}
        />
      }
      </>
    );
  }
  
  const buildToolbar = () => {
    return (
      <>
      {props.richText && preview && !props.disabled &&
        <div 
          className={`toolbar-toggle text-button ${toolbar || toolbarHover ? "text-button-toggled" : ""} k-icon k-font-icon k-i-edit-tools`}
          title="Zobraziť toolbar"
          onClick={() => {
            if (toolbar) setToolbarHover(false);
            setToolbar(!toolbar);
          }}
          onMouseEnter={() => {
            clearTimeout(toolbarTimeout.current);
            setToolbarHover(true);
          }}
          onMouseLeave={() => {
            // checking if toolbar is hovered over
            let fc = editorRef.current?._element?.firstChild;
            if (fc && fc.role === "toolbar") {
              fc.onmouseover = () => {
                clearTimeout(toolbarTimeout.current);
              }
              fc.onmouseleave = () => {
                if (!fc.contains(document.activeElement)) {
                  clearTimeout(toolbarTimeout.current);
                  toolbarTimeout.current = setTimeout(() => setToolbarHover(false), 300);
                } else {
                  // close popup after selecting format
                  document.activeElement.onblur = () => {
                    clearTimeout(toolbarTimeout.current);
                    toolbarTimeout.current = setTimeout(() => setToolbarHover(false), 300);
                  }
                }
              }
            }

            clearTimeout(toolbarTimeout.current);
            toolbarTimeout.current = setTimeout(() => setToolbarHover(false), 500);
          }}
        >
        </div>
      }
      </>
    );
  }

  return (
    <>
      {props.defaultExpanded ? (
        !props.richText || (props.richText && !preview) ? (

        <div className={`form-input textarea ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")}`}>
          <textarea
            className="k-textarea"
            placeholder={props.placeholder}
            value={value}
            //value={props.value !== undefined && props.value !== null ? props.value : value}
            // disabled={this.props.disabled}
            readOnly={props.disabled}
            onChange={(e) => {
              const value = e.target.value.replace(/[\u200B-\u200D\uFEFF]/g, '');
              if (props.readOnly || (props.maxLength && value.length >= props.maxLength)) return;
              setValue(value);
              setEditorValue(defaultMarkdownParser.parse(value));
              props.onChange(props.name, value);
              setValidationValue(value);
            }}
            onBlur={onBlur}
          />
          {props.maxLength && (
            <div className="charCounter">
              <span
                className={value?.length === props.maxLength - 1 ? 'charCounterRed' : ''}
              >
                {value?.length || props.value?.length || 0}/{props.maxLength}
              </span>
            </div>
          )}
          {buildSwitch()}

        </div> 
        ) : (
          <div className={`form-input textarea ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")}`}>
            <Editor 
              tools={[[Bold, Italic, Link, Unlink, InsertImage], [Undo, Redo], [OrderedList, UnorderedList], CustomFormatBlock]}
              value={editorValue ?? ""}
              contentEditable={false}
              defaultEditMode="div"
              className={toolbar || toolbarHover ? "show-toolbar" : ""}
              ref={editorRef}
              onChange={(e) => {
                if (props.disabled) return;
                let value = defaultMarkdownSerializer.serialize(e.value).replace(/[\u200B-\u200D\uFEFF]/g, '');
                if (props.readOnly || (props.maxLength && value.length >= props.maxLength)) return;
                setValue(value);
                setEditorValue(e.value);
                props.onChange(props.name, value);
                setValidationValue(value);
              }}
            />
            {buildSwitch()}
            {buildToolbar()}
        </div>
        )
      ) : (
        <>
          <Input
            className={`form-input textarea ${props.disabled ? "disabled-field" : props.required ? "required-field" : (props.recommended ? "recommended-field" : "default-field")}`}
            name={props.name}
            value={!!props.value ? props.value.replace(/[\n]/g, ' ') : props.value}
            disabled={props.disabled}
            onChange={handleChange}
            onClick={handleClick}
            required={props.required}
            tabIndex={props.tabIndex}
            minLength={props.minLength}
            maxLength={props.maxLength}
            onBlur={onBlur}
            placeholder={props.placeholder}
            title={props.name || props.title}
          />
          {props.enableInsertValue && (
            <Button
              icon={'paste'}
              onClick={handleInsertClick}
              title={'Vložiť opis CG HD'}
            />
          )}
          <Dialog
            isOpen={isOpen}
            onClose={handleClose}
            onConfirm={handleConfirm}
            title={props.title}
          >
            <div className="textarea">
              <textarea
                className="k-textarea"
                value={dialogValue || ''}
                // disabled={this.props.disabled}
                readOnly={props.disabled}
                onChange={(e) => {
                  const value = e.target.value.replace(/[\u200B-\u200D\uFEFF]/g, '');
                  if (props.readOnly || (props.maxLength && value.length >= props.maxLength))
                    return;
                  setDialogValue(value);
                }}
              />
            </div>
          </Dialog>
        </>
      )}
    </>
  );
};


TextareaComponent.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
  readOnly: PropTypes.bool,
  value: PropTypes.string,
  required: PropTypes.bool,
  title: PropTypes.string.isRequired,
  tabIndex: PropTypes.number,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  enableInsertValue: PropTypes.bool,
  insertValue: PropTypes.string,
};

export default TextareaComponent;